 import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import axios from 'axios';
import SignaturePad from 'signature_pad';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

// Stripe configuration is now loaded dynamically from the backend inside InvoicePanel.

// ─── Status pipeline ──────────────────────────────────────────────────────────
const STATUSES = ['pending_signature', 'contract_signed', 'payment_received', 'confirmed'];
const STATUS_LABELS = { pending_signature: 'Awaiting Signature', contract_signed: 'Contract Signed', payment_received: 'Payment Sent', confirmed: 'Confirmed', cancelled: 'Cancelled' };
const STATUS_COLORS = { pending_signature: '#f5a623', contract_signed: '#9F5CFF', payment_received: '#27C85A', confirmed: '#27C85A', cancelled: '#E34364' };

function BookingStatusPanel({ booking, onCancel, fetchBookings }) {
  const { user, setUser } = useAuth();
  const status = booking?.status || 'pending_signature';
  const isDone = status === 'confirmed';
  const isCancelled = status === 'cancelled';
  const isCancelledByCustomer = isCancelled && booking?.admin_message === 'Cancelled by Customer';

  const [editOpen, setEditOpen] = useState(false);
  const [customerName, setCustomerName] = useState(user?.name || '');
  const [phoneNumber, setPhoneNumber] = useState(booking?.phone_number || '');
  const [venueName, setVenueName] = useState(booking?.venue_name || '');
  const [venueAddress, setVenueAddress] = useState(booking?.venue_address || '');
  const [photoStripStyle, setPhotoStripStyle] = useState(booking?.photo_strip_style || '2×6 (Vertical)');
  const [stripWordings, setStripWordings] = useState(booking?.strip_wordings || '');
  const [eventTheme, setEventTheme] = useState(booking?.event_theme_description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (booking) {
      setCustomerName(user?.name || '');
      setPhoneNumber(booking.phone_number || '');
      setVenueName(booking.venue_name || '');
      setVenueAddress(booking.venue_address || '');
      setPhotoStripStyle(booking.photo_strip_style || '2×6 (Vertical)');
      setStripWordings(booking.strip_wordings || '');
      setEventTheme(booking.event_theme_description || '');
    }
  }, [booking, user]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await axios.patch(`${API}/api/bookings/${booking.id}`, {
        customer_name: customerName,
        phone_number: phoneNumber,
        venue_name: venueName,
        venue_address: venueAddress,
        photo_strip_style: photoStripStyle,
        strip_wordings: stripWordings,
        event_theme_description: eventTheme,
      }, { withCredentials: true });

      if (user) {
        setUser(prev => ({ ...prev, name: customerName }));
      }

      if (fetchBookings) await fetchBookings();
      setEditOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save booking updates');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>Booking Status</h2>

      {!booking ? (
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 32, textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ fontSize: 40, margin: '0 0 12px' }}>📸</p>
          <p style={{ color: '#a0a0b8', fontSize: 14 }}>No active booking found.</p>
          <a href="/book" style={{ display: 'inline-block', marginTop: 12, background: '#E34364', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 700, padding: '12px 28px', borderRadius: 50 }}>
            Book Now →
          </a>
        </div>
      ) : (
        <>
          {/* Status pipeline */}
          {!isCancelled && (
            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: '20px 24px', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 20 }}>
              <div className="no-scrollbar" style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 0 }}>
                {STATUSES.map((s, i) => {
                  const idx = STATUSES.indexOf(status);
                  const done = i < idx || isDone;
                  const active = s === status;
                  return (
                    <React.Fragment key={s}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 80 }}>
                        <div style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, background: done || active ? STATUS_COLORS[s] : 'rgba(255,255,255,0.06)', color: '#fff', border: active ? `3px solid ${STATUS_COLORS[s]}` : 'none', boxShadow: active ? `0 0 20px ${STATUS_COLORS[s]}66` : 'none', transition: 'all 0.3s' }}>
                          {done && !active ? '✓' : i + 1}
                        </div>
                        <span style={{ fontSize: 10, color: active ? '#fff' : '#555', marginTop: 6, textAlign: 'center', lineHeight: 1.3 }}>{STATUS_LABELS[s]}</span>
                      </div>
                      {i < STATUSES.length - 1 && (
                        <div style={{ flex: 1, height: 2, background: done ? STATUS_COLORS[s] : 'rgba(255,255,255,0.08)', margin: '0 0 20px', transition: 'background 0.3s' }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cancelled state */}
          {isCancelled && (
            <div style={{ background: isCancelledByCustomer ? 'rgba(255,255,255,0.02)' : 'rgba(227,67,100,0.08)', border: `1px solid ${isCancelledByCustomer ? 'rgba(255,255,255,0.1)' : 'rgba(227,67,100,0.3)'}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: isCancelledByCustomer ? '#a0a0b8' : '#E34364', fontWeight: 700 }}>
                {isCancelledByCustomer ? '✕ Booking Cancelled' : '❌ Booking Declined'}
              </p>
              {!isCancelledByCustomer && booking.admin_message && (
                <div style={{ borderLeft: '3px solid #E34364', paddingLeft: 12, marginTop: 10 }}>
                  <p style={{ margin: '0 0 4px', fontSize: 11, color: '#a0a0b8' }}>Message from our team:</p>
                  <p style={{ margin: 0, fontSize: 14, color: '#e0e0e0', lineHeight: 1.7 }}>"{booking.admin_message}"</p>
                </div>
              )}
              <a href="/book" style={{ display: 'inline-block', marginTop: 16, color: '#9F5CFF', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>Submit a new request →</a>
            </div>
          )}

          {/* Booking details */}
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: '4px 20px', border: '1px solid rgba(255,255,255,0.06)' }}>
            {(() => {
              const isTaxExempt = Boolean(booking.is_tax_exempt);
              const totalVal = parseFloat(booking.quoted_price || 0);
              const subtotalVal = (booking.subtotal && parseFloat(booking.subtotal) > 0)
                ? parseFloat(booking.subtotal)
                : (isTaxExempt ? totalVal : parseFloat((totalVal / 1.0825).toFixed(2)));
              const taxVal = isTaxExempt
                ? 0
                : ((booking.tax !== undefined && booking.tax !== null)
                  ? parseFloat(booking.tax)
                  : parseFloat((totalVal - subtotalVal).toFixed(2)));

              return [
                ['Customer Name', user?.name],
                ['Event Date', booking.event_date],
                ['Time', `${booking.start_time} – ${booking.end_time}`],
                booking.phone_number ? ['Phone Number', booking.phone_number] : null,
                booking.venue_name ? ['Venue Name', booking.venue_name] : null,
                ['Venue Address', booking.venue_address || '—'],
                ['Booth Type', booking.booth_type],
                ['Photo Strip Size', booking.photo_strip_style || '2×6 (Vertical)'],
                ['Backdrop', booking.backdrop?.name || 'No Backdrop / TBD'],
                ['Duration', booking.duration_hours ? `${booking.duration_hours} hours` : '—'],
                booking.strip_wordings ? ['Strip Wordings', booking.strip_wordings] : null,
                booking.event_theme_description ? ['Theme / Details', booking.event_theme_description] : null,
                ['Subtotal', `$${subtotalVal.toFixed(2)}`],
                isTaxExempt
                  ? ['Sales Tax', '$0.00 (Tax Free / Exempt)']
                  : ['Sales Tax (8.25%)', `$${taxVal.toFixed(2)}`],
                ['Total Quoted Price', `$${totalVal.toFixed(2)}`]
              ];
            })().filter(Boolean).map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: 13, color: '#a0a0b8' }}>{l}</span>
                <span style={{ fontSize: 13, color: l === 'Sales Tax' && v.includes('Tax Free') ? '#27C85A' : '#fff', textAlign: 'right', maxWidth: '60%', fontWeight: l === 'Total Quoted Price' ? 700 : (l === 'Sales Tax' && v.includes('Tax Free') ? 700 : 400) }}>{v}</span>
              </div>
            ))}
          </div>

          {/* Edit button */}
          {!isCancelled && (
            <button
              onClick={() => setEditOpen(true)}
              style={{
                marginTop: 20,
                width: '100%',
                background: 'rgba(159,92,255,0.05)',
                border: '1px solid rgba(159,92,255,0.2)',
                borderRadius: 10,
                color: '#9F5CFF',
                padding: '12px 0',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: 10,
              }}
              onMouseEnter={e => { e.target.style.background = 'rgba(159,92,255,0.1)'; e.target.style.borderColor = 'rgba(159,92,255,0.4)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(159,92,255,0.05)'; e.target.style.borderColor = 'rgba(159,92,255,0.2)'; }}
            >
              📝 Edit Booking Details
            </button>
          )}

          {/* Cancel button */}
          {['pending_signature', 'contract_signed'].includes(booking.status) && (
            <button
              onClick={() => onCancel(booking.id)}
              style={{
                marginTop: 10,
                width: '100%',
                background: 'rgba(227,67,100,0.05)',
                border: '1px solid rgba(227,67,100,0.2)',
                borderRadius: 10,
                color: '#E34364',
                padding: '12px 0',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.target.style.background = 'rgba(227,67,100,0.1)'; e.target.style.borderColor = 'rgba(227,67,100,0.4)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(227,67,100,0.05)'; e.target.style.borderColor = 'rgba(227,67,100,0.2)'; }}
            >
              ✕ Cancel Booking Request
            </button>
          )}

          {/* Edit Modal */}
          {editOpen && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ background: '#101428', borderRadius: 20, padding: 28, border: '1px solid rgba(159,92,255,0.3)', width: '100%', maxWidth: 500, boxShadow: '0 20px 50px rgba(0,0,0,0.5)', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ margin: '0 0 16px', color: '#fff', fontSize: 18, fontWeight: 700 }}>Edit Booking Details</h3>
                {error && <p style={{ color: '#E34364', fontSize: 13, margin: '0 0 12px' }}>⚠️ {error}</p>}
                <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Customer Name</label>
                    <input value={customerName} onChange={e => setCustomerName(e.target.value)} required style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Phone Number</label>
                    <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Venue Name</label>
                    <input value={venueName} onChange={e => setVenueName(e.target.value)} style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Venue Address</label>
                    <input value={venueAddress} onChange={e => setVenueAddress(e.target.value)} style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#f5a623', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Photo Strip Size / Style</label>
                    <select value={photoStripStyle} onChange={e => setPhotoStripStyle(e.target.value)} style={{ width: '100%', background: '#101428', border: '1px solid rgba(245,166,35,0.4)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }}>
                      <option value="2×6 (Vertical)" style={{ background: '#101428' }}>2×6 (Vertical)</option>
                      <option value="4×6 (Horizontal)" style={{ background: '#101428' }}>4×6 (Horizontal)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Strip Wordings</label>
                    <input value={stripWordings} onChange={e => setStripWordings(e.target.value)} placeholder="e.g. Happy Birthday Sarah!" style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Event Theme / Special Instructions</label>
                    <textarea value={eventTheme} onChange={e => setEventTheme(e.target.value)} placeholder="Describe event theme, color preferences, etc." style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 12px', color: '#fff', fontSize: 13, outline: 'none', height: 80, resize: 'vertical', fontFamily: 'inherit' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                    <button type="button" onClick={() => setEditOpen(false)} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 10, padding: '12px 0', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                    <button type="submit" disabled={saving} style={{ flex: 1, background: '#9F5CFF', border: 'none', color: '#fff', borderRadius: 10, padding: '12px 0', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
                      {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Contract panel ───────────────────────────────────────────────────────────
function ContractPanel({ booking, fetchBookings }) {
  const navigate = useNavigate();
  const [sigMode, setSigMode] = useState('draw'); // 'draw' | 'type'
  const [typedName, setTypedName] = useState('');
  const [signing, setSigning] = useState(false);
  const [signedUrl, setSignedUrl] = useState(null);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    if (sigMode === 'draw' && canvasRef.current) {
      padRef.current = new SignaturePad(canvasRef.current, { penColor: '#E34364', backgroundColor: 'rgba(0,0,0,0)' });
    }
    return () => { if (padRef.current) padRef.current.off(); };
  }, [sigMode]);

  useEffect(() => {
    if (booking?.Contract?.signed_at) {
      setSignedUrl(booking.Contract.pdf_url);
    } else {
      setSignedUrl(null);
    }
    setError('');
    setTypedName('');
    if (padRef.current) {
      try {
        padRef.current.clear();
      } catch (_) {}
    }
  }, [booking]);

  if (!booking) return <p style={{ color: '#a0a0b8', fontSize: 14 }}>No booking found.</p>;
  if (!booking.Contract?.pdf_url) {
    return (
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 24, border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
        <p style={{ color: '#a0a0b8', fontSize: 14 }}>
          {booking.status === 'pending_signature' ? '⏳ Your contract is being generated. Please check back in a moment.' : 'No contract found.'}
        </p>
      </div>
    );
  }

  const handleSign = async () => {
    let sigData = null;
    if (sigMode === 'draw') {
      if (!padRef.current || padRef.current.isEmpty()) { setError('Please draw your signature'); return; }
      sigData = padRef.current.toDataURL('image/png');
    } else {
      if (!typedName.trim()) { setError('Please type your name'); return; }
    }
    setError(''); setSigning(true);
    try {
      const res = await axios.post(`${API}/api/bookings/${booking.id}/sign`, { signatureDataUrl: sigData, signerName: typedName }, { withCredentials: true });
      setSignedUrl(res.data.pdfUrl);
      if (fetchBookings) fetchBookings();
      setTimeout(() => {
        navigate('/dashboard/invoice');
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign contract');
    } finally {
      setSigning(false);
    }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>My Contract</h2>
      {/* PDF preview */}
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 20 }}>
        <iframe src={booking.Contract.pdf_url} title="Contract" style={{ width: '100%', height: 500, border: 'none', display: 'block' }} />
      </div>

      {signedUrl || booking.Contract?.signed_at ? (
        <div style={{ background: 'rgba(39,200,90,0.08)', border: '1px solid rgba(39,200,90,0.3)', borderRadius: 14, padding: 20, textAlign: 'center' }}>
          <p style={{ color: '#27C85A', fontWeight: 700, fontSize: 15, margin: '0 0 12px' }}>✅ Contract Signed!</p>
          <a href={signedUrl || booking.Contract.pdf_url} target="_blank" rel="noreferrer" download style={{ display: 'inline-block', background: '#E34364', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 700, padding: '12px 28px', borderRadius: 50 }}>
            Download Signed Contract ↓
          </a>
        </div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 24, border: '1px solid rgba(255,255,255,0.06)' }}>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: '#e0e0e0', fontWeight: 600 }}>Sign Your Contract</p>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {['draw', 'type'].map(m => (
              <button key={m} onClick={() => setSigMode(m)} style={{ flex: 1, background: sigMode === m ? '#E34364' : 'rgba(255,255,255,0.05)', color: sigMode === m ? '#fff' : '#a0a0b8', border: `1px solid ${sigMode === m ? '#E34364' : 'rgba(255,255,255,0.1)'}`, borderRadius: 10, padding: '10px 0', fontSize: 13, cursor: 'pointer', fontWeight: sigMode === m ? 700 : 400 }}>
                {m === 'draw' ? '✏️ Draw Signature' : '⌨️ Type Name'}
              </button>
            ))}
          </div>
          {sigMode === 'draw' ? (
            <div>
              <canvas ref={canvasRef} width={560} height={160} style={{ width: '100%', height: 160, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 10, display: 'block', cursor: 'crosshair' }} />
              <button onClick={() => padRef.current?.clear()} style={{ marginTop: 8, background: 'none', border: 'none', color: '#a0a0b8', fontSize: 12, cursor: 'pointer' }}>Clear</button>
            </div>
          ) : (
            <input value={typedName} onChange={e => setTypedName(e.target.value)} placeholder="Type your full name" style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '14px', color: '#E34364', fontSize: 20, fontStyle: 'italic', outline: 'none', boxSizing: 'border-box' }} />
          )}
          {error && <p style={{ color: '#E34364', fontSize: 12, marginTop: 8 }}>{error}</p>}
          <button onClick={handleSign} disabled={signing} style={{ width: '100%', marginTop: 16, background: signing ? '#555' : '#E34364', color: '#fff', border: 'none', borderRadius: 50, padding: '14px 0', fontSize: 14, fontWeight: 700, cursor: signing ? 'not-allowed' : 'pointer' }}>
            {signing ? 'Signing…' : 'Sign & Confirm Booking →'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Stripe Checkout Form ───────────────────────────────────────────────────
function StripeCheckoutForm({ booking, onPaymentSuccess, paymentType = 'deposit' }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // 1. Create PaymentIntent on the backend
      const res = await axios.post(`${API}/api/payments/create-intent`, {
        bookingId: booking.id,
        paymentType
      }, { withCredentials: true });

      const { clientSecret } = res.data;

      // 2. Confirm the card payment with Stripe using CardNumberElement
      const cardNumberElement = elements.getElement(CardNumberElement);
      const { paymentIntent, error: stripeErr } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            name: booking.customer?.name || 'Customer'
          }
        }
      });

      if (stripeErr) {
        setError(stripeErr.message || 'Payment failed');
        setLoading(false);
        return;
      }

      // 3. Confirm with the backend
      if (paymentIntent.status === 'succeeded') {
        const verifyRes = await axios.post(`${API}/api/payments/confirm-payment`, {
          paymentIntentId: paymentIntent.id,
          bookingId: booking.id
        }, { withCredentials: true });

        if (verifyRes.data.success) {
          setSuccess(true);
          if (onPaymentSuccess) {
            onPaymentSuccess();
          }
        } else {
          setError('Payment succeeded but verification failed. Please contact support.');
        }
      } else {
        setError('Payment was not successful. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to complete payment.');
    } finally {
      setLoading(false);
    }
  };

  // Derived values
  const total = parseFloat(booking.quoted_price || 0);
  const extrasTotal = booking.extras_paid ? 0 : parseFloat(booking.extras_total || 0);
  const payAmount = parseFloat(paymentType === 'partial' ? (total / 2).toFixed(2) : (total + extrasTotal).toFixed(2));

  const successMsg = paymentType === 'partial'
    ? `Payment sent! Pay the remaining balance of $${((total / 2) + extrasTotal).toFixed(2)} no later than 7 days before your event. 🎉`
    : 'Full payment sent! Your booking is confirmed! 🎉';
  const buttonLabel = paymentType === 'partial' ? `Pay 50% Now — $${payAmount.toFixed(2)}` : `Pay in Full — $${payAmount.toFixed(2)}`;
  const introMsg = paymentType === 'partial' ? 'Paying 50% now:' : 'Paying in full:';

  if (success) {
    return (
      <div style={{ marginTop: 24, padding: 24, background: 'rgba(39,200,90,0.1)', border: '1px solid rgba(39,200,90,0.3)', borderRadius: 16, textAlign: 'center', backdropFilter: 'blur(10px)' }}>
        <p style={{ margin: '0 0 6px', fontSize: 20, color: '#27C85A', fontWeight: 700 }}>✓ Payment Successful!</p>
        <p style={{ margin: 0, fontSize: 13, color: '#e0e0e0' }}>{successMsg}</p>
      </div>
    );
  }



  const elementOptions = {
    style: {
      base: {
        color: '#fff',
        fontSize: '14px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        '::placeholder': {
          color: 'rgba(255,255,255,0.3)'
        }
      },
      invalid: {
        color: '#E34364'
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{
      marginTop: 24,
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14,
      overflow: 'hidden'
    }}>

      {/* Card Mockup — sober dark slate */}
      <div style={{
        background: 'linear-gradient(135deg, #1e2235 0%, #252a40 100%)',
        padding: '20px 24px',
        position: 'relative',
        overflow: 'hidden',
        height: 130,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.05)'
      }}>
        {/* Subtle circle accents */}
        <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -20, left: 60, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.02)', pointerEvents: 'none' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 2, textTransform: 'uppercase' }}>Pixly Booth</span>
          {/* Mastercard-style circles */}
          <div style={{ display: 'flex' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }} />
            <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', marginLeft: -8 }} />
          </div>
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', letterSpacing: 4, fontFamily: 'monospace', marginBottom: 10 }}>
            ••••  ••••  ••••  ••••
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <p style={{ margin: 0, fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1 }}>Cardholder</p>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{booking.customer?.name || 'Customer'}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 9, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1 }}>Amount</p>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#fff', fontWeight: 700 }}>${payAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form body */}
      <div style={{ padding: '20px 24px' }}>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#8a8faa' }}>
          {introMsg} <strong style={{ color: '#c8cbd8', fontWeight: 600 }}>${payAmount.toFixed(2)}</strong>{' '}
          to {paymentType === 'partial' ? 'partially secure your booking.' : 'fully secure your booking.'}
        </p>

        {/* Card Number */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Card Number</label>
          <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
            <CardNumberElement options={elementOptions} />
          </div>
        </div>

        {/* Expiry + CVC */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Expiry</label>
            <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
              <CardExpiryElement options={elementOptions} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>CVC</label>
            <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
              <CardCvcElement options={elementOptions} />
            </div>
          </div>
        </div>

        {error && (
          <p style={{ color: '#e05a6f', fontSize: 12, margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚠️ {error}
          </p>
        )}

        <button type="submit" disabled={loading || !stripe} style={{
          width: '100%',
          background: loading ? 'rgba(255,255,255,0.05)' : 'rgba(99,120,255,0.85)',
          color: loading ? '#555' : '#fff',
          border: loading ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(99,120,255,0.4)',
          borderRadius: 9,
          padding: '13px 0',
          fontSize: 14,
          fontWeight: 600,
          cursor: loading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          letterSpacing: 0.3
        }}>
          {loading ? 'Processing…' : buttonLabel}
        </button>

        <p style={{ margin: '12px 0 0', fontSize: 11, color: '#4a4f62', textAlign: 'center' }}>
          🔒 Secured by Stripe · 256-bit SSL encrypted
        </p>
      </div>
    </form>
  );
}




// ─── Unified Stripe Checkout Form ──────────────────────────────────────────
function UnifiedStripeCheckoutForm({ booking, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const amountRemaining = parseFloat(booking.amount_remaining || 0);
  const extrasTotal = booking.extras_paid ? 0 : parseFloat(booking.extras_total || 0);
  const totalOutstanding = parseFloat((amountRemaining + extrasTotal).toFixed(2));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/api/payments/create-unified-intent`, {
        bookingId: booking.id,
      }, { withCredentials: true });

      const { clientSecret } = res.data;
      const cardEl = elements.getElement(CardNumberElement);
      const { paymentIntent, error: stripeErr } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardEl, billing_details: { name: booking.customer?.name || 'Customer' } },
      });

      if (stripeErr) { setError(stripeErr.message); setLoading(false); return; }

      if (paymentIntent.status === 'succeeded') {
        await axios.post(`${API}/api/payments/confirm-unified-payment`, {
          paymentIntentId: paymentIntent.id,
          bookingId: booking.id,
        }, { withCredentials: true });
        setSuccess(true);
        if (onSuccess) onSuccess();
      } else {
        setError('Payment was not successful. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete payment.');
    } finally {
      setLoading(false);
    }
  };

  const elementOptions = {
    style: {
      base: { color: '#fff', fontSize: '14px', fontFamily: 'system-ui, sans-serif', '::placeholder': { color: 'rgba(255,255,255,0.3)' } },
      invalid: { color: '#E34364' },
    },
  };

  if (success) {
    return (
      <div style={{ padding: 20, background: 'rgba(39,200,90,0.1)', border: '1px solid rgba(39,200,90,0.3)', borderRadius: 12, textAlign: 'center', marginTop: 16 }}>
        <p style={{ margin: '0 0 4px', fontSize: 18, color: '#27C85A', fontWeight: 700 }}>✓ Balance Paid!</p>
        <p style={{ margin: 0, fontSize: 13, color: '#a0a0b8' }}>Thank you — your outstanding balance has been received.</p>
      </div>
    );
  }

  // Determine button label
  let buttonLabel = `💳 Pay Outstanding Balance — $${totalOutstanding.toFixed(2)}`;
  if (amountRemaining > 0 && extrasTotal > 0) {
    buttonLabel = `💳 Pay Remaining Balance & Extras — $${totalOutstanding.toFixed(2)}`;
  } else if (extrasTotal > 0) {
    buttonLabel = `💳 Pay Extras — $${totalOutstanding.toFixed(2)}`;
  } else {
    buttonLabel = `💳 Pay Remaining Balance — $${totalOutstanding.toFixed(2)}`;
  }

  return (
    <form onSubmit={handleSubmit} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20, marginTop: 16 }}>
      <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Card Number</label>
          <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
            <CardNumberElement options={elementOptions} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>Expiry</label>
            <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
              <CardExpiryElement options={elementOptions} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: '#6b7080', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>CVC</label>
            <div style={{ padding: '11px 14px', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
              <CardCvcElement options={elementOptions} />
            </div>
          </div>
        </div>
      </div>
      {error && <p style={{ color: '#e05a6f', fontSize: 12, margin: '0 0 14px' }}>⚠️ {error}</p>}
      <button type="submit" disabled={loading || !stripe} style={{ width: '100%', background: loading ? 'rgba(255,255,255,0.05)' : '#27C85A', color: loading ? '#555' : '#fff', border: 'none', borderRadius: 9, padding: '13px 0', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
        {loading ? 'Processing…' : buttonLabel}
      </button>
      <p style={{ margin: '10px 0 0', fontSize: 11, color: '#4a4f62', textAlign: 'center' }}>🔒 Secured by Stripe · 256-bit SSL encrypted</p>
    </form>
  );
}

// ─── Invoice panel ────────────────────────────────────────────────────────────
function InvoicePanel({ booking, fetchBookings }) {
  const [selectedPayType, setSelectedPayType] = useState('full');
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeError, setStripeError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/payments/config`)
      .then(res => {
        const key = res.data.publishableKey;
        if (key) {
          setStripePromise(loadStripe(key));
        } else {
          setStripeError(true);
        }
      })
      .catch(err => {
        console.error("Failed to load Stripe config:", err);
        setStripeError(true);
      });
  }, []);

  if (!booking) return <p style={{ color: '#a0a0b8' }}>No booking found.</p>;

  const total = parseFloat(booking.quoted_price || 0);
  const amountPaid = parseFloat(booking.amount_paid || 0);
  const amountRemaining = parseFloat(booking.amount_remaining || 0);
  const eventDateParts = (booking.event_date || '').split('-').map(Number);
  const eventDate = booking.event_date ? new Date(eventDateParts[0], eventDateParts[1] - 1, eventDateParts[2]) : null;
  const finalPayDate = eventDate ? new Date(eventDateParts[0], eventDateParts[1] - 1, eventDateParts[2] - 7) : null;
  const isPaid = ['payment_received', 'confirmed'].includes(booking.status);
  const isContractSigned = booking.status === 'contract_signed';

  // Extras
  let extras = booking.booking_extras || [];
  if (typeof extras === 'string') {
    try {
      extras = JSON.parse(extras);
    } catch (_) {
      extras = [];
    }
  }
  if (!Array.isArray(extras)) {
    extras = [];
  }
  const extrasTotal = parseFloat(booking.extras_total || 0);
  const extrasPaid = booking.extras_paid === true || booking.extras_paid === 1;
  const hasUnpaidExtras = extras.length > 0 && extrasTotal > 0 && !extrasPaid;

  const renderPaymentSection = () => {
    if (isPaid) {
      const totalOutstanding = amountRemaining + (extrasPaid ? 0 : extrasTotal);
      return (
        <div style={{ marginTop: 24, padding: 20, background: 'rgba(39,200,90,0.08)', border: '1px solid rgba(39,200,90,0.3)', borderRadius: 12 }}>
          <p style={{ margin: '0 0 8px', fontSize: 16, color: '#27C85A', fontWeight: 700 }}>
            {totalOutstanding === 0 ? '✅ All Payments Completed!' : '💳 Outstanding Balance due'}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: '#a0a0b8' }}>Amount Paid: <strong style={{ color: '#fff' }}>${amountPaid.toFixed(2)}</strong> &nbsp;|&nbsp; Type: <strong style={{ color: '#fff' }}>{booking.payment_type === 'partial' ? '50% Deposit' : 'Paid in Full'}</strong></p>
          {totalOutstanding > 0 && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#f5a623' }}>⚠️ Balance remaining: <strong>${totalOutstanding.toFixed(2)}</strong></p>
              <div style={{ background: 'rgba(0,0,0,0.15)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                {amountRemaining > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                    <span style={{ color: '#a0a0b8' }}>Remaining Booking Balance:</span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>${amountRemaining.toFixed(2)}</span>
                  </div>
                )}
                {hasUnpaidExtras && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#f5a623' }}>Outstanding Extras:</span>
                    <span style={{ color: '#f5a623', fontWeight: 600 }}>${extrasTotal.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6, marginTop: 6 }}>
                  <span style={{ color: '#fff' }}>Total Due:</span>
                  <span style={{ color: '#E34364' }}>${totalOutstanding.toFixed(2)}</span>
                </div>
              </div>

              {!stripePromise && !stripeError ? (
                <div style={{ color: '#a0a0b8', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>Loading payment gateway...</div>
              ) : stripePromise ? (
                <Elements stripe={stripePromise}>
                  <UnifiedStripeCheckoutForm booking={booking} onSuccess={fetchBookings} />
                </Elements>
              ) : (
                <div style={{ padding: 14, background: 'rgba(227,67,100,0.08)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 12, textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 12, color: '#E34364' }}>⚠️ Stripe not loaded. Please contact support.</p>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    if (isContractSigned) {
      const fullAmount = total + (extrasPaid ? 0 : extrasTotal);
      const remainingAmountIfPartial = (total / 2) + (extrasPaid ? 0 : extrasTotal);

      return (
        <div style={{ marginTop: 24 }}>
          <p style={{ margin: '0 0 14px', fontSize: 14, color: '#fff', fontWeight: 600 }}>Choose Payment Option</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
            {[
              { val: 'full', label: 'Pay in Full', sub: `$${fullAmount.toFixed(2)} — booking confirmed immediately` },
              { val: 'partial', label: 'Pay 50% Now', sub: `$${(total / 2).toFixed(2)} now + $${remainingAmountIfPartial.toFixed(2)} due 7 days before event` }
            ].map(opt => (
              <div key={opt.val} onClick={() => setSelectedPayType(opt.val)}
                style={{ cursor: 'pointer', borderRadius: 12, border: `2px solid ${selectedPayType === opt.val ? '#9F5CFF' : 'rgba(255,255,255,0.1)'}`, padding: '14px 16px', background: selectedPayType === opt.val ? 'rgba(159,92,255,0.1)' : 'rgba(255,255,255,0.02)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${selectedPayType === opt.val ? '#9F5CFF' : '#555'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {selectedPayType === opt.val && <div style={{ width: 9, height: 9, borderRadius: '50%', background: '#9F5CFF' }} />}
                </div>
                <div>
                  <p style={{ margin: '0 0 2px', fontSize: 14, color: '#fff', fontWeight: 600 }}>{opt.label}</p>
                  <p style={{ margin: 0, fontSize: 12, color: '#a0a0b8' }}>{opt.sub}</p>
                </div>
              </div>
            ))}
          </div>
          {!stripePromise && !stripeError ? (
            <div style={{ color: '#a0a0b8', fontSize: 13, textAlign: 'center', padding: '10px 0' }}>Loading payment gateway...</div>
          ) : stripePromise ? (
            <Elements stripe={stripePromise}>
              <StripeCheckoutForm booking={booking} onPaymentSuccess={fetchBookings} paymentType={selectedPayType} />
            </Elements>
          ) : (
            <div style={{ padding: 14, background: 'rgba(227,67,100,0.08)', border: '1px solid rgba(227,67,100,0.3)', borderRadius: 12, textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#E34364' }}>⚠️ Stripe not loaded. Please contact support.</p>
            </div>
          )}
        </div>
      );
    }
    return (
      <div style={{ marginTop: 24, padding: 16, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#a0a0b8' }}>Payment will be available after you sign your contract.</p>
      </div>
    );
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>Invoice & Payment</h2>

      {hasUnpaidExtras && (
        <div style={{
          background: 'rgba(245,166,35,0.08)',
          border: '1px solid rgba(245,166,35,0.3)',
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 20,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12
        }}>
          <div>
            <span style={{ fontSize: 13, color: '#f5a623', fontWeight: 700, display: 'block' }}>
              ⚠️ Outstanding Extras Invoice Pending
            </span>
            <span style={{ fontSize: 11, color: '#a0a0b8', marginTop: 4, display: 'block' }}>
              Our team has added extra items to your event. These are added to your outstanding balance below.
            </span>
          </div>
          <button
            onClick={() => {
              const el = document.getElementById('payment-section-wrapper');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            style={{
              background: '#f5a623',
              border: 'none',
              borderRadius: 8,
              color: '#0a0a14',
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'transform 0.15s'
            }}
            onMouseEnter={e => e.target.style.transform = 'scale(1.03)'}
            onMouseLeave={e => e.target.style.transform = 'scale(1)'}
          >
            Pay Balance ↓
          </button>
        </div>
      )}

      {(() => {
        const isTaxExempt = Boolean(booking.is_tax_exempt);
        const subtotalVal = (booking.subtotal && parseFloat(booking.subtotal) > 0)
          ? parseFloat(booking.subtotal)
          : (isTaxExempt ? total : parseFloat((total / 1.0825).toFixed(2)));
        const taxVal = isTaxExempt
          ? 0
          : ((booking.tax !== undefined && booking.tax !== null)
            ? parseFloat(booking.tax)
            : parseFloat((total - subtotalVal).toFixed(2)));

        return [
          ['Subtotal', `$${subtotalVal.toFixed(2)}`],
          isTaxExempt ? ['Sales Tax', '$0.00 (Tax Free / Exempt)'] : ['Sales Tax (8.25%)', `$${taxVal.toFixed(2)}`],
          ['Total Quoted Price', `$${total.toFixed(2)}`],
          ['Event Date', booking.event_date || '—'],
          ['Booth Type', booking.booth_type || '—'],
          ['Payment Deadline', finalPayDate?.toDateString() || '—']
        ].map(([l, v]) => (
          <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontSize: 13, color: '#a0a0b8' }}>{l}</span>
            <span style={{ fontSize: 14, color: l === 'Sales Tax' && v.includes('Tax Free') ? '#27C85A' : '#fff', fontWeight: l === 'Total Quoted Price' ? 700 : (l === 'Sales Tax' && v.includes('Tax Free') ? 700 : 600) }}>{v}</span>
          </div>
        ));
      })()}
      <div id="payment-section-wrapper">
        {renderPaymentSection()}
      </div>

      {/* ── Outstanding Extras Section ─────────────────────────────────────── */}
      {extras.length > 0 && (
        <div id="outstanding-extras-section" style={{ marginTop: 28 }}>
          <div style={{ padding: '10px 16px', background: extrasPaid ? 'rgba(39,200,90,0.08)' : 'rgba(245,166,35,0.08)', border: `1px solid ${extrasPaid ? 'rgba(39,200,90,0.3)' : 'rgba(245,166,35,0.3)'}`, borderRadius: 10, marginBottom: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: extrasPaid ? '#27C85A' : '#f5a623', fontWeight: 700 }}>
              {extrasPaid ? '✅ Extras Paid — Thank you!' : '⚠️ Outstanding Extras Invoice Pending'}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a0a0b8' }}>
              {extrasPaid ? `A payment of $${extrasTotal.toFixed(2)} was received for extras.` : `Our team has added extra items to your event. A payment of $${extrasTotal.toFixed(2)} is due.`}
            </p>
          </div>

          {/* Extras line items */}
          <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
            {extras.map((it, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: i < extras.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <div>
                  <span style={{ fontSize: 13, color: '#e0e0e0', fontWeight: 600 }}>{it.name}</span>
                  <span style={{ fontSize: 11, color: '#555', marginLeft: 8 }}>×{it.quantity} @ ${parseFloat(it.unit_price).toFixed(2)}</span>
                </div>
                <span style={{ fontSize: 14, color: '#27C85A', fontWeight: 700 }}>${parseFloat(it.total).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 16px', background: extrasPaid ? 'rgba(39,200,90,0.06)' : 'rgba(245,166,35,0.06)', borderTop: `1px solid ${extrasPaid ? 'rgba(39,200,90,0.2)' : 'rgba(245,166,35,0.2)'}` }}>
              <span style={{ fontSize: 15, color: extrasPaid ? '#27C85A' : '#f5a623', fontWeight: 800 }}>Total: ${extrasTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Notifications panel ──────────────────────────────────────────────────────
function NotificationsPanel({ setUnread }) {
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/notifications`, { withCredentials: true })
      .then(r => { setNotifications(r.data.notifications || []); setUnread(0); }).catch(() => {});
  }, []);

  const markRead = async (id) => {
    await axios.patch(`${API}/api/notifications/${id}/read`, {}, { withCredentials: true });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 18, color: '#fff', fontWeight: 700 }}>Notifications</h2>
      {notifications.length === 0 ? (
        <p style={{ color: '#a0a0b8', fontSize: 14 }}>No notifications yet.</p>
      ) : (
        notifications.map(n => (
          <div key={n.id} onClick={() => !n.read && markRead(n.id)}
            style={{ background: n.read ? 'rgba(255,255,255,0.02)' : 'rgba(227,67,100,0.08)', border: `1px solid ${n.read ? 'rgba(255,255,255,0.06)' : 'rgba(227,67,100,0.2)'}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, cursor: n.read ? 'default' : 'pointer', transition: 'all 0.2s' }}>
            <p style={{ margin: '0 0 4px', fontSize: 14, color: '#fff' }}>{n.message}</p>
            <p style={{ margin: 0, fontSize: 11, color: '#a0a0b8' }}>{new Date(n.created_at).toLocaleString()}</p>
            {!n.read && <span style={{ display: 'inline-block', marginTop: 6, background: '#E34364', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 50 }}>New</span>}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Main Dashboard page ──────────────────────────────────────────────────────
export default function Dashboard({ tab, setUnread }) {
  const { socket } = useSocket();
  const [bookings, setBookings] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    const saved = localStorage.getItem('pixly_selected_booking_id');
    return saved ? parseInt(saved, 10) : null;
  });
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/bookings/mine`, { withCredentials: true });
      const list = r.data.bookings || [];
      setBookings(list);
      if (list.length > 0) {
        setSelectedId(prev => {
          const valid = prev && list.some(b => b.id === prev);
          const activeId = valid ? prev : list[0].id;
          localStorage.setItem('pixly_selected_booking_id', activeId);
          return activeId;
        });
      } else {
        setSelectedId(null);
        localStorage.removeItem('pixly_selected_booking_id');
      }
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Real-time status updates
  useEffect(() => {
    if (!socket) return;
    const handleStatus = () => {
      fetchBookings();
    };
    const handleContract = () => {
      fetchBookings();
    };
    socket.on('status_update', handleStatus);
    socket.on('contract_ready', handleContract);
    return () => {
      socket.off('status_update', handleStatus);
      socket.off('contract_ready', handleContract);
    };
  }, [socket, fetchBookings]);

  const selectBooking = (id) => {
    setSelectedId(id);
    localStorage.setItem('pixly_selected_booking_id', id);
  };

  const handleCancel = (id) => {
    setConfirmModal({
      title: "Cancel Booking Request",
      message: "Are you sure you want to cancel this booking request? This action is final and cannot be undone.",
      confirmText: "Yes, Cancel Request",
      confirmColor: "#E34364",
      onConfirm: async () => {
        try {
          await axios.patch(`${API}/api/bookings/${id}/cancel`, {}, { withCredentials: true });
          fetchBookings();
        } catch (err) {
          alert(err.response?.data?.error || "Failed to cancel booking");
        }
      }
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#a0a0b8' }}>Loading…</div>;

  const booking = bookings.find(b => b.id === selectedId) || null;

  const renderContentPanel = () => {
    if (tab === 'contract') return <ContractPanel booking={booking} fetchBookings={fetchBookings} />;
    if (tab === 'invoice') return <InvoicePanel booking={booking} fetchBookings={fetchBookings} />;
    if (tab === 'notifications') return <NotificationsPanel setUnread={setUnread} />;
    return <BookingStatusPanel booking={booking} onCancel={handleCancel} fetchBookings={fetchBookings} />;
  };

  if (bookings.length === 0) {
    return (
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 48, textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p style={{ fontSize: 48, margin: '0 0 16px' }}>📸</p>
        <p style={{ color: '#a0a0b8', fontSize: 15, fontWeight: 600 }}>No bookings found under your account.</p>
        <p style={{ color: '#555', fontSize: 13, margin: '8px 0 24px' }}>Ready to light up your event? Book a custom photo booth with us today!</p>
        <a href="/book" style={{ display: 'inline-block', background: '#E34364', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 700, padding: '14px 36px', borderRadius: 50, boxShadow: '0 4px 14px rgba(227,67,100,0.4)' }}>
          Book Now →
        </a>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 24, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <style>{`
        @media (max-width: 992px) {
          #bookings-sidebar { width: 100% !important; flex: none !important; margin-bottom: 20px; }
          #bookings-detail-view { width: 100% !important; }
        }
      `}</style>
      
      {/* Sidebar: Multiple Bookings Selector list */}
      <div id="bookings-sidebar" style={{ flex: '0 0 280px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 16, boxSizing: 'border-box' }}>
        <h3 style={{ margin: '0 0 16px', color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>My Bookings ({bookings.length})</span>
          <a href="/book" style={{ color: '#E34364', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>+ New</a>
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '500px', overflowY: 'auto', paddingRight: 4 }}>
          {bookings.map(b => {
            const isSel = b.id === selectedId;
            const bStatus = b.status || 'pending_signature';
            const displayLabel = STATUS_LABELS[bStatus] || bStatus;
            const displayColor = STATUS_COLORS[bStatus] || '#a0a0b8';
            
            return (
              <div 
                key={b.id} 
                onClick={() => selectBooking(b.id)}
                style={{
                  background: isSel ? 'rgba(227,67,100,0.1)' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isSel ? '#E34364' : 'rgba(255,255,255,0.05)'}`,
                  borderRadius: 12,
                  padding: 14,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>#{b.id} - {b.booth_type}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: displayColor }}>{displayLabel}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#a0a0b8' }}>
                  <span>📅 {b.event_date}</span>
                  <span>💰 ${parseFloat(b.quoted_price || 0).toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Main Panel Detail View */}
      <div id="bookings-detail-view" style={{ flex: 1, minWidth: 280 }}>
        {renderContentPanel()}
      </div>
      {/* Custom Confirmation Modal */}
      {confirmModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#101428', borderRadius: 16, padding: 28, border: `1px solid ${confirmModal.confirmColor || '#E34364'}44`, width: '100%', maxWidth: 440, boxShadow: `0 10px 30px rgba(0,0,0,0.5)` }}>
            <h3 style={{ margin: '0 0 12px', color: '#fff', fontSize: 18, fontWeight: 700 }}>{confirmModal.title}</h3>
            <p style={{ margin: '0 0 24px', color: '#a0a0b8', fontSize: 14, lineHeight: '1.5' }}>{confirmModal.message}</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmModal(null)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 10, padding: '10px 20px', fontSize: 13, cursor: 'pointer', transition: 'all 0.2s' }}>Cancel</button>
              <button onClick={async () => {
                await confirmModal.onConfirm();
                setConfirmModal(null);
              }} style={{ background: confirmModal.confirmColor, border: 'none', color: '#fff', borderRadius: 10, padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>{confirmModal.confirmText}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
